import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import { FormHandles } from '@unform/core';
import { AuthLayout } from '../../../../components/AuthLayout';
import { AuthFormHeader } from '../../../../components/AuthFormHeader';
import { InputCheckboxBox } from '../../../../components/InputCheckboxBox';
import { getValidationErrors } from '../../../../utils/getValidationErrors';

import {
  Banner,
  ButtonSubmit,
  ContainerCheckboxBox,
  ExistingUserTag,
  Form,
  Info,
  Input,
  Row2,
  SectionTitle,
  BackLink,
} from './styles';
import { phoneMask } from '../../../../utils/inputMasks';
import { api } from '../../../../services/api';
import { useToast } from '../../../../hooks/toast';
import { useModal } from '../../../../hooks/modal';
import { InfoNewGym } from '../../../../modais/infoNewGym';

interface FormProps {
  name_gym: string;
  phone_gym: string;
  address_gym: string;
  fullname_user: string;
  nickname_user: string;
  email_user: string;
  min_age: boolean;
  privacity: boolean;
  terms_accepted: boolean;
}

interface ICheckEmailResponse {
  exists: boolean;
  fullname?: string;
  nickname?: string;
}

const NewGym: React.FC = () => {
  const formRef = useRef<FormHandles>(null);
  const navigate = useNavigate();
  const [enableDisableSubmit, setenableDisableSubmit] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [existingUser, setExistingUser] = useState(false);

  const { addToast } = useToast();
  const { addModal } = useModal();

  const handleBlurEmail = useCallback(async () => {
    const email = formRef.current?.getFieldValue('email_user');

    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      return;
    }

    try {
      setCheckingEmail(true);

      const response = await api.get<ICheckEmailResponse>('gyms/check-email', {
        params: { email: email.toLowerCase() },
      });

      if (response.data.exists) {
        setExistingUser(true);
        formRef.current?.setFieldValue('fullname_user', response.data.fullname);
        formRef.current?.setFieldValue('nickname_user', response.data.nickname);
      } else {
        setExistingUser(false);
      }
    } catch {
      // Falha na checagem não deve travar o preenchimento manual --
      // simplesmente segue como se o email fosse novo.
      setExistingUser(false);
    } finally {
      setCheckingEmail(false);
    }
  }, []);

  useEffect(() => {
    document.title = 'Cadastro - TrenzoX';
  }, []);

  const handleEnableOrDisableSubmit = useCallback(() => {
    setenableDisableSubmit(
      formRef.current?.getFieldValue('min_age') &&
      formRef.current?.getFieldValue('privacity') &&
      formRef.current?.getFieldValue('terms_accepted'),
    );
  }, []);

  const handleSubmit = useCallback(
    async (data: FormProps): Promise<void> => {
      window.scrollTo(0, 0);

      try {
        formRef.current?.setErrors({});

        const schema = yup.object().shape({
          name_gym: yup.string().required('Esse campo é obrigatório.'),
          phone_gym: yup
            .string()
            .nullable()
            .transform((value: string, o: string) => (o === '' ? null : value))
            .min(10, 'Insira o seu telefone com DDD'),
          address_gym: yup.string(),
          fullname_user: yup.string().required('Esse campo é obrigatório.'),
          nickname_user: yup.string().required('Esse campo é obrigatório.'),
          email_user: yup
            .string()
            .email('Insira um email válido.')
            .required('Esse campo é obrigatório.'),
          min_age: yup.boolean().required(),
          privacity: yup.boolean().required(),
          terms_accepted: yup.boolean().required(),
        });

        await schema.validate(data, { abortEarly: false });

        addToast({
          type: 'info',
          title: 'Processando dados',
          description: 'Aguarde...',
        });

        const response = await api.post<{ existing_account: boolean }>(
          '/gyms',
          {
            nameGym: data.name_gym,
            addressGym: data.address_gym || undefined,
            phoneGym: data.phone_gym || undefined,
            fullnameUser: data.fullname_user,
            nicknameUser: data.nickname_user,
            emailUser: data.email_user,
            minAge: !!data.min_age,
            privacity: !!data.privacity,
            termsAccepted: !!data.terms_accepted,
          },
        );

        formRef.current?.reset();
        setExistingUser(false);

        addModal(
          <InfoNewGym
            existingAccount={response.data.existing_account}
            gymName={data.name_gym}
          />,
        );
      } catch (err: any) {
        if (err instanceof yup.ValidationError) {
          const errors = getValidationErrors(err);
          formRef.current?.setErrors(errors);

          addToast({
            type: 'error',
            title: 'Por favor, verifique os dados informados',
          });

          return;
        }

        if (err?.response?.status === 403) {
          addToast({
            type: 'error',
            title: 'Email ja cadastrado na plataforma',
            description: 'Tente recuperar a sua de senha na página de login.',
          });
          return;
        }

        addToast({
          type: 'error',
          title: 'Houve um erro na criação da academia',
          description: 'Verifique os seus dados.',
        });
      }
    },
    [addToast],
  );

  return (
    <AuthLayout
      wide
      eyebrow="Alta performance"
      title={
        <>
          EVOLUA A GESTÃO <span>DA SUA ACADEMIA</span>
        </>
      }
      subtitle="Junte-se ao ecossistema de gestão esportiva e acompanhamento financeiro para academias de alta performance."
    >
      <AuthFormHeader
        badge="Teste grátis por 15 dias"
        title="Crie o cadastro da sua academia"
        subtitle="Insira as informações da instituição e do gestor responsável. O nível de acesso pode ser alterado a qualquer momento na plataforma."
      />

      <Banner>
        <div>
          <p className="tag">Promoção ativa</p>
          <p className="text">
            Disponibilizamos um período de teste de 15 dias.
          </p>
        </div>
      </Banner>

      <Form ref={formRef} onSubmit={handleSubmit}>
        <SectionTitle label="01 · Informações da academia" />

        <Input
          name="name_gym"
          label="Nome da academia"
          placeholder="Exemplo Fitness"
        />

        <Row2>
          <Input
            name="phone_gym"
            label="Telefone da academia"
            placeholder="Telefone com DDD"
            type="tel"
            maxLength={11}
            onBlur={() => {
              formRef.current?.setFieldValue(
                'phone_gym',
                phoneMask(formRef.current?.getFieldValue('phone_gym')),
              );
            }}
            onChange={() => {
              formRef.current?.setFieldValue(
                'phone_gym',
                formRef.current?.getFieldValue('phone_gym').replace(/\D/g, ''),
              );
            }}
          />
          <Input
            name="address_gym"
            label="Endereço da academia"
            placeholder="Rua, Número, Cidade - Estado"
          />
        </Row2>

        <SectionTitle label="02 · Informações do gestor" />

        <Input
          name="email_user"
          label="Email do gestor"
          placeholder="johndoe@exemplo.com"
          onBlur={handleBlurEmail}
        />

        {checkingEmail && <Info text="Verificando email..." />}

        {existingUser && (
          <ExistingUserTag>
            Esse email já tem cadastro na plataforma. A academia nova será
            vinculada à sua conta existente, com as mesmas permissões de gestor.
          </ExistingUserTag>
        )}

        <div
          style={
            existingUser ? { opacity: 0.5, pointerEvents: 'none' } : undefined
          }
        >
          <Row2>
            <Input
              name="fullname_user"
              label="Nome do gestor"
              placeholder="John Doe"
              readOnly={existingUser}
            />
            <Input
              name="nickname_user"
              label="Como você quer ser chamado?"
              placeholder="John"
              readOnly={existingUser}
            />
          </Row2>
        </div>

        <ContainerCheckboxBox>
          <InputCheckboxBox
            name="min_age"
            id="min_age"
            onChange={handleEnableOrDisableSubmit}
          />
          <label htmlFor="min_age">Tenho mais de 18 anos de idade.</label>
        </ContainerCheckboxBox>

        <ContainerCheckboxBox>
          <InputCheckboxBox
            name="privacity"
            id="privacity"
            onChange={handleEnableOrDisableSubmit}
          />
          <label htmlFor="privacity">
            Li e aceito as{' '}
            <a
              href={`${process.env.REACT_APP_SITE_URL}/privacy`}
              target="_blank"
              rel="noopener noreferrer"
            >
              políticas de privacidade
            </a>{' '}
            da plataforma TRENZOX.
          </label>
        </ContainerCheckboxBox>

        <ContainerCheckboxBox>
          <InputCheckboxBox
            name="terms_accepted"
            id="terms_accepted"
            onChange={handleEnableOrDisableSubmit}
          />
          <label htmlFor="terms_accepted">
            Li e aceito os{' '}
            <a
              href={`${process.env.REACT_APP_SITE_URL}/terms`}
              target="_blank"
              rel="noopener noreferrer"
            >
              termos de uso
            </a>{' '}
            da plataforma TRENZOX.
          </label>
        </ContainerCheckboxBox>

        <ButtonSubmit
          name="submit"
          value="Iniciar meu teste gratuito"
          disabled={!enableDisableSubmit}
        />

        <BackLink>
          Já tem uma conta?{' '}
          <button type="button" onClick={() => navigate('/sign-in')}>
            Faça login
          </button>
        </BackLink>
      </Form>
    </AuthLayout>
  );
};

export { NewGym };
